import { yupResolver } from '@hookform/resolvers/yup'
import React, { FunctionComponent, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import * as yup from 'yup'

import { useAppSelector } from '../../App/Hooks/reduxHooks'
import { Button } from '../../Common/Button'
import { Flex } from '../../Common/Flex'
import { Select } from '../../Common/Select'
import { Spinner } from '../../Common/Spinner'
import { TextInput } from '../../Common/TextInput'
import { ErrorMessage } from '../../HealthInsuranceN/style'
import { IAPIAccountUpgrade } from '../interface/IAPIAccountUpgrade'
import { ISubscriptionPlan } from '../interface/ISubscriptionPlan'
import { subscribe } from '../subscriptionAPICall'

import { IBillingInformation } from './IBillingInformation'
const Form = Flex.withComponent('form')

const schema = yup
  .object({
    fullName: yup.string().required(),
    address: yup.string().required(),
    city: yup.string().required(),
    zip: yup.string().required(),
    country: yup.string().required(),
    bic: yup
      .string()
      .test('bic-custom', 'Invalid BIC', (value: string, ctx) => {
        if (!/^[a-z]{6}[2-9a-z][0-9a-np-z]([a-z0-9]{3}|x{3})?$/i.test(value)) {
          return ctx.createError({ message: 'Invalid IBAN' })
        }

        return true
      }),
    iban: yup
      .string()
      // .min(4)
      .test('iban-custom', 'Invalid IBAN', (value: string, ctx) => {
        let $v = value.replace(/[^A-Z0-9]/g, '')
        // Regex source: http://ht5ifv.serprest.pt/extensions/tools/IBAN/index.html
        const isValid =
          /^AL\d{10}[0-9A-Z]{16}$|^AD\d{10}[0-9A-Z]{12}$|^AT\d{18}$|^BH\d{2}[A-Z]{4}[0-9A-Z]{14}$|^BE\d{14}$|^BA\d{18}$|^BG\d{2}[A-Z]{4}\d{6}[0-9A-Z]{8}$|^HR\d{19}$|^CY\d{10}[0-9A-Z]{16}$|^CZ\d{22}$|^DK\d{16}$|^FO\d{16}$|^GL\d{16}$|^DO\d{2}[0-9A-Z]{4}\d{20}$|^EE\d{18}$|^FI\d{16}$|^FR\d{12}[0-9A-Z]{11}\d{2}$|^GE\d{2}[A-Z]{2}\d{16}$|^DE\d{20}$|^GI\d{2}[A-Z]{4}[0-9A-Z]{15}$|^GR\d{9}[0-9A-Z]{16}$|^HU\d{26}$|^IS\d{24}$|^IE\d{2}[A-Z]{4}\d{14}$|^IL\d{21}$|^IT\d{2}[A-Z]\d{10}[0-9A-Z]{12}$|^[A-Z]{2}\d{5}[0-9A-Z]{13}$|^KW\d{2}[A-Z]{4}22!$|^LV\d{2}[A-Z]{4}[0-9A-Z]{13}$|^LB\d{6}[0-9A-Z]{20}$|^LI\d{7}[0-9A-Z]{12}$|^LT\d{18}$|^LU\d{5}[0-9A-Z]{13}$|^MK\d{5}[0-9A-Z]{10}\d{2}$|^MT\d{2}[A-Z]{4}\d{5}[0-9A-Z]{18}$|^MR13\d{23}$|^MU\d{2}[A-Z]{4}\d{19}[A-Z]{3}$|^MC\d{12}[0-9A-Z]{11}\d{2}$|^ME\d{20}$|^NL\d{2}[A-Z]{4}\d{10}$|^NO\d{13}$|^PL\d{10}[0-9A-Z]{,16}n$|^PT\d{23}$|^RO\d{2}[A-Z]{4}[0-9A-Z]{16}$|^SM\d{2}[A-Z]\d{10}[0-9A-Z]{12}$|^SA\d{4}[0-9A-Z]{18}$|^RS\d{20}$|^SK\d{22}$|^SI\d{17}$|^ES\d{22}$|^SE\d{22}$|^CH\d{7}[0-9A-Z]{12}$|^TN59\d{20}$|^TR\d{7}[0-9A-Z]{17}$|^AE\d{21}$|^GB\d{2}[A-Z]{4}\d{14}$/.test(
            $v,
          )

        if (!isValid) {
          return ctx.createError({ message: 'Invalid IBAN' })
        }

        $v = value.replace(/[^A-Z0-9]/g, '').replace(/^(.{4})(.*)$/, '$2$1') // Move the first 4 chars from left to the right
        $v = $v.replace(
          /[A-Z]/g,
          $e => $e.charCodeAt(0) - 'A'.charCodeAt(0) + 10,
        ) // Convert A-Z to 10-25
        let $sum = 0
        let $ei = 1 // First exponent
        for (let $i = $v.length - 1; $i >= 0; $i--) {
          $sum += $ei * parseInt($v.charAt($i), 10) // multiply the digit by it's exponent
          $ei = ($ei * 10) % 97 // compute next base 10 exponent  in modulus 97
        }

        if ($sum % 97 == 1) {
          return true
        }

        return ctx.createError({ message: 'Invalid IBAN' })
      })
      .required(),
    phone: yup.string().required(),
  })
  .required()

type IBillingFormData = yup.InferType<typeof schema>

export const InvoiceDataForm: FunctionComponent<{
  billingInformation: IBillingInformation
  plan: ISubscriptionPlan
  onSuccess: () => void
}> = ({ billingInformation, plan, onSuccess }) => {
  const { t } = useTranslation()
  const history = useHistory()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const textInputMargin = '10px 2px'

  const chosenPlan = useAppSelector(state => state.subscriptionPlan)

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<IBillingFormData>({
    defaultValues: {
      fullName: billingInformation.name,
      address: billingInformation.address,
      zip: billingInformation.postal,
      city: billingInformation.location,
      country: billingInformation.vat.substring(0, 2).toUpperCase(),
    },
    resolver: yupResolver(schema),
    mode: 'onSubmit',
  })

  const onSubmit = async (data: IBillingFormData) => {
    setIsLoading(true)
    const apiObject: IAPIAccountUpgrade = {
      vat: billingInformation.vat,
      module_id: plan.moduleId,
      invoice_name: data.fullName,
      billing_address: data.address,
      city: data.city,
      country_code: data.country as 'BE' | 'NL' | 'DE',
      postal_code: data.zip,
      iban: data.iban,
      bic: data.bic,
      phone: data.phone,
    }

    try {
      await subscribe(apiObject)
      onSuccess()
    } catch (e) {
      setIsLoading(false)
    }
  }

  if (!chosenPlan?.title) history.push('/subscription')

  if (isLoading) {
    return <Spinner />
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Flex flexDirection={'column'}>
        <TextInput readOnly value={billingInformation.vat} />
        <ErrorMessage>{errors.fullName?.message}</ErrorMessage>
        <Controller
          name="fullName"
          control={control}
          render={({ field }) => (
            <TextInput
              margin={textInputMargin}
              placeholder={t('Full name')}
              type="text"
              {...field}
            />
          )}
        />
        <ErrorMessage>{errors.fullName?.message}</ErrorMessage>
        <Controller
          name="address"
          control={control}
          render={({ field }) => (
            <TextInput
              margin={textInputMargin}
              type="text"
              placeholder={t('Address')}
              {...field}
            />
          )}
        />
        <ErrorMessage>{errors.address?.message}</ErrorMessage>
        <Flex>
          <Controller
            name="city"
            control={control}
            render={({ field }) => (
              <TextInput
                margin={textInputMargin}
                placeholder={t('City')}
                type="text"
                {...field}
              />
            )}
          />
          <Controller
            name="zip"
            control={control}
            render={({ field }) => (
              <TextInput
                margin={textInputMargin}
                placeholder={t('Zip')}
                type="text"
                {...field}
              />
            )}
          />
        </Flex>
        <ErrorMessage>{errors.city?.message}</ErrorMessage>
        <ErrorMessage>{errors.zip?.message}</ErrorMessage>
        <Controller
          name="country"
          control={control}
          render={({ field }) => (
            <Select
              {...field}
              options={[
                {
                  label: t('Belgium'),
                  value: 'BE',
                },
                {
                  label: t('The Netherlands'),
                  value: 'NL',
                },
              ]}
            />
          )}
        />
        <ErrorMessage>{errors.country?.message}</ErrorMessage>
        <Controller
          name="iban"
          control={control}
          render={({ field }) => (
            <TextInput
              margin={textInputMargin}
              placeholder={t('Iban')}
              type="text"
              {...field}
            />
          )}
        />
        <ErrorMessage>{errors.iban?.message}</ErrorMessage>
        <Controller
          name="bic"
          control={control}
          render={({ field }) => (
            <TextInput
              margin={textInputMargin}
              placeholder={t('Bic')}
              type="text"
              {...field}
            />
          )}
        />
        <ErrorMessage>{errors.bic?.message}</ErrorMessage>
        <Controller
          name="phone"
          control={control}
          render={({ field }) => (
            <TextInput
              margin={textInputMargin}
              placeholder={t('Phone')}
              type="text"
              {...field}
            />
          )}
        />
        <ErrorMessage>{errors.phone?.message}</ErrorMessage>
        <div>
          <Button mt={3} type={'submit'}>
            {t('Upgrade')}
          </Button>
        </div>
      </Flex>
    </Form>
  )
}
